import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnSizingState,
  type RowSelectionState,
  type VisibilityState,
} from '@tanstack/react-table';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { ArrowDown, ArrowUp, Boxes, Check, Package, ChevronLeft, ChevronRight, Columns3, Download, Plus, RefreshCw, Search, X } from 'lucide-react';
import { Modal } from './CatalogSettings.tsx';
import { MasterSyncBulk } from '../components/MasterSync.tsx';
import { NewProductDialog } from './NewProduct.tsx';
import type { CatalogSummary, CategoryCount, Page, ProductRow } from '@berytus/contracts';
import { api, brl, int } from '../api.ts';
import { usePreferences } from '../prefs.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, MatchTag, Tag, Thumb } from '../components/ui.tsx';
import { AttrFilterButton } from '../components/AttrFilter.tsx';
import { ContentSyncDialog, type ContentSelection } from '../components/ContentSync.tsx';
import { can } from '@berytus/contracts';

const TABLE_ID = 'catalog';
const CHANNEL_PRICES: [string, string][] = [['mercadolivre', 'Meli'], ['shopee', 'Shopee'], ['ifood', 'iFood']];
const PAGE_SIZES = [25, 50, 100, 200];

/** Exportar para planilha (CSV que abre no Excel): catálogo completo com os canais, ou só preço e estoque por canal. */
function ExportMenu() {
  const go = (kind: 'full' | 'offers', inactive = false) => {
    window.location.href = `/api/v1/exports/catalog.csv?kind=${kind}${inactive ? '&inactive=1' : ''}`;
  };
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button className="btn" type="button"><Download size={16} /> Exportar</button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content className="menu" align="end" sideOffset={6}>
          <Dropdown.Item className="menu-item" onSelect={() => go('full')}>Catálogo com canais (anunciado, preço e estoque)</Dropdown.Item>
          <Dropdown.Item className="menu-item" onSelect={() => go('offers')}>Só preço e estoque por canal</Dropdown.Item>
          <Dropdown.Item className="menu-item" onSelect={() => go('full', true)}>Catálogo com canais, incluindo fora de linha</Dropdown.Item>
          <Dropdown.Item className="menu-item" onSelect={() => { window.location.href = '/api/v1/exports/listings.csv?status=active'; }}>Anúncios ativos de todos os canais</Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

/** Título para exibir: produto sem título na origem aparece como "Sem nome". */
export const titleOf = (p: { title: string | null }) => p.title ?? 'Sem nome';

type SortKey = 'title' | 'sku' | 'gtin' | 'category' | 'status' | 'available' | 'cost' | 'pmc';

interface ColMeta {
  label: string;
  sort?: SortKey;
  num?: boolean;
  /** colunas fixas à esquerda: ficam visíveis ao rolar para o lado */
  pin?: boolean;
  required?: boolean;
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> extends ColMeta {}
}

export function Catalog() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { table: tablePrefs, updateTable } = usePreferences();
  const tp = tablePrefs(TABLE_ID);
  // PMC só existe em farmácia; nos outros segmentos a coluna começa escondida.
  const me = useMe();
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState<ContentSelection | null | false>(false);
  const [kitting, setKitting] = useState<ContentSelection | false>(false);
  const [mastering, setMastering] = useState<{ productIds: string[]; label: string } | false>(false);
  const defaultHidden = me.tenant.vertical === 'pharmacy' ? [] : ['pmc'];
  const hiddenCols = tp.hidden ?? defaultHidden;

  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const sort = (params.get('sort') as SortKey | null) ?? 'title';
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const pageSize = tp.pageSize ?? 50;

  function setUrl(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? p.set(k, v) : p.delete(k));
    setParams(p, { replace: true });
  }

  // A busca vai para a URL 300 ms depois da última tecla (dá para compartilhar o link).
  useEffect(() => {
    const t = setTimeout(() => search !== q && setUrl({ q: search || null, page: null }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    status: params.get('status') ?? 'all',
    active: params.get('active') ?? 'true',
    sort,
    dir,
    ...(q ? { q } : {}),
    ...(params.get('category') ? { category: params.get('category')! } : {}),
    ...(params.get('attr') ? { attr: params.get('attr')! } : {}),
    ...(params.get('attrs') ? { attrs: params.get('attrs')! } : {}),
    ...(params.get('costMin') ? { costMin: params.get('costMin')! } : {}),
    ...(params.get('costMax') ? { costMax: params.get('costMax')! } : {}),
  });

  const list = useQuery({
    queryKey: ['products', query.toString()],
    queryFn: () => api<Page<ProductRow>>(`/catalog/products?${query}`),
    placeholderData: keepPreviousData,
  });
  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api<CategoryCount[]>('/catalog/categories') });
  const summary = useQuery({ queryKey: ['summary'], queryFn: () => api<CatalogSummary>('/catalog/summary') });

  // ---------- colunas ----------
  const columns = useMemo<ColumnDef<ProductRow>[]>(
    () => [
      {
        id: 'select',
        size: 40,
        enableResizing: false,
        meta: { label: 'Selecionar', pin: true, required: true },
        header: ({ table }) => (
          <input
            type="checkbox"
            className="check"
            aria-label="Selecionar todos desta página"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => { if (el) el.indeterminate = table.getIsSomeRowsSelected(); }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="check"
            aria-label={`Selecionar ${row.original.sku}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        id: 'image',
        size: 56,
        enableResizing: false,
        meta: { label: 'Foto', pin: true },
        header: () => <span className="sr-only">Foto</span>,
        cell: ({ row }) => <Thumb src={row.original.imageUrl} alt="" />,
      },
      {
        id: 'title',
        size: 260,
        minSize: 180,
        meta: { label: 'Nome', sort: 'title', pin: true, required: true },
        header: () => 'Nome',
        cell: ({ row }) => {
          const p = row.original;
          return (
            <>
              <Link to={`/catalogo/${p.id}`} onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }} className={p.title ? undefined : 'untitled'} title={titleOf(p)}>
                {titleOf(p)}
              </Link>
              {!p.active && <> <Tag tone="off">Inativo</Tag></>}
            </>
          );
        },
      },
      { id: 'sku', size: 100, meta: { label: 'SKU', sort: 'sku' }, header: () => 'SKU', cell: ({ row }) => <span className="code">{row.original.sku}</span> },
      { id: 'gtin', size: 132, meta: { label: 'EAN', sort: 'gtin' }, header: () => 'EAN', cell: ({ row }) => <span className="code">{row.original.gtin ?? '—'}</span> },
      { id: 'category', size: 140, meta: { label: 'Categoria', sort: 'category' }, header: () => 'Categoria', cell: ({ row }) => row.original.category ?? '—' },
      { id: 'status', size: 120, meta: { label: 'Vínculo', sort: 'status' }, header: () => 'Vínculo', cell: ({ row }) => <MatchTag status={row.original.matchStatus} /> },
      { id: 'available', size: 96, meta: { label: 'Disponível', sort: 'available', num: true }, header: () => 'Disponível', cell: ({ row }) => int(row.original.available) },
      { id: 'cost', size: 104, meta: { label: 'Custo', sort: 'cost', num: true }, header: () => 'Custo', cell: ({ row }) => brl(row.original.cost) },
      { id: 'pmc', size: 96, meta: { label: 'PMC', sort: 'pmc', num: true }, header: () => 'PMC', cell: ({ row }) => brl(row.original.pmc) },
      ...CHANNEL_PRICES.map(([code, label]): ColumnDef<ProductRow> => ({
        id: `price_${code}`, size: 104, meta: { label: `Preço ${label}`, num: true }, header: () => `Preço ${label}`,
        cell: ({ row }) => (row.original.channelPrices?.[code] ? brl(row.original.channelPrices[code]!) : '—'),
      })),
    ],
    [],
  );

  // ---------- estado da tabela (preferências do usuário) ----------
  const [sizing, setSizing] = useState<ColumnSizingState>(tp.sizes ?? {});
  const [selection, setSelection] = useState<RowSelectionState>({});
  const visibility: VisibilityState = Object.fromEntries(hiddenCols.map((id) => [id, false]));

  // Larguras são salvas depois que o usuário solta a borda da coluna.
  const sizingSaved = useRef(sizing);
  useEffect(() => {
    if (JSON.stringify(sizing) === JSON.stringify(sizingSaved.current)) return;
    const t = setTimeout(() => {
      sizingSaved.current = sizing;
      updateTable(TABLE_ID, { sizes: Object.fromEntries(Object.entries(sizing).map(([k, v]) => [k, Math.round(v)])) });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizing]);

  // Seleção vale para a página atual; muda de filtro ou página, limpa.
  useEffect(() => setSelection({}), [query.toString()]);

  const data = useMemo(() => list.data?.items ?? [], [list.data]);
  const table = useReactTable({
    data,
    columns,
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    columnResizeMode: 'onChange',
    state: { columnSizing: sizing, columnVisibility: visibility, rowSelection: selection },
    onColumnSizingChange: setSizing,
    onRowSelectionChange: setSelection,
    enableRowSelection: true,
  });

  const toggleColumn = (id: string, show: boolean) => {
    const hidden = new Set(hiddenCols);
    show ? hidden.delete(id) : hidden.add(id);
    updateTable(TABLE_ID, { hidden: [...hidden] });
  };

  // Mesma coluna: inverte a direção. Outra coluna: começa crescente. Padrão (nome crescente) não polui a URL.
  const onSort = (key: SortKey) => {
    const nextDir = sort === key && dir === 'asc' ? 'desc' : 'asc';
    setUrl({ sort: key === 'title' ? null : key, dir: nextDir === 'desc' ? 'desc' : null, page: null });
  };

  // A coluna Nome ocupa a sobra de largura da tela; colunas fixas à esquerda calculam a posição pelas visíveis.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [wrapWidth, setWrapWidth] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWrapWidth(Math.floor(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [list.isPending]);
  const visible = table.getVisibleLeafColumns();
  const baseTotal = visible.reduce((n, c) => n + c.getSize(), 0);
  const extra = Math.max(0, wrapWidth - baseTotal);
  const widthOf = (id: string, size: number) => (id === 'title' ? size + extra : size);
  const pinLeft: Record<string, number> = {};
  {
    let left = 0;
    for (const c of visible) {
      if (!c.columnDef.meta?.pin) continue;
      pinLeft[c.id] = left;
      left += widthOf(c.id, c.getSize());
    }
  }

  const total = list.data?.total ?? 0;
  // Sincronizar pelo filtro atual (sem filtro: o catálogo ativo inteiro); com marcados, eles.
  function filterSelection(): ContentSelection {
    if (Object.keys(selection).length) return { productIds: Object.keys(selection), label: `${Object.keys(selection).length} selecionado(s)` };
    const f: Record<string, string> = {};
    for (const k of ['q', 'category', 'status', 'active', 'attr', 'attrs', 'costMin', 'costMax']) { const v = query.get(k); if (v) f[k] = v; }
    const filtered = !!(q || params.get('category') || params.get('status') || params.get('attr') || params.get('attrs') || params.get('costMin') || params.get('costMax'));
    return { filter: f, label: filtered ? `Filtro atual (${int(total)})` : `Catálogo ativo (${int(total)})` };
  }
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const selected = Object.keys(selection).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Catálogo</h1>
          <p>
            Todos os produtos enviados para a Berytus, comparados pelo EAN com o catálogo mestre.
            {summary.data && <> {int(summary.data.active)} ativos.</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ExportMenu />
          {can(me.tenant.access, 'catalog', 'write') && (
            <button className="btn" type="button" onClick={() => setSyncing(filterSelection())}><RefreshCw size={16} /> Sincronizar anúncios</button>
          )}
          {can(me.tenant.access, 'catalog', 'write') && (
            <button className="btn" type="button" onClick={() => setKitting(filterSelection())}><Boxes size={16} /> Criar kits</button>
          )}
          {can(me.tenant.access, 'catalog', 'write') && (
            <button className="btn primary" type="button" onClick={() => setCreating(true)}><Plus size={16} /> Novo produto</button>
          )}
        </div>
        {kitting !== false && <BulkKitDialog selection={kitting} onClose={() => setKitting(false)} />}
        {mastering !== false && <MasterSyncBulk productIds={mastering.productIds} label={mastering.label} onClose={() => setMastering(false)} />}
        {creating && <NewProductDialog onClose={() => setCreating(false)} />}
        {syncing !== false && <ContentSyncDialog selection={syncing} onClose={() => setSyncing(false)} />}
      </div>

      <div className="toolbar">
        <label className="search">
          <Search size={16} />
          <span className="sr-only">Buscar</span>
          <input className="input" type="search" placeholder="SKU, EAN ou nome (vários: separe por vírgula)" value={search} onChange={(e) => setSearch(e.target.value)}
            title="Vários de uma vez: separe por vírgula, ou cole uma coluna do Excel"
            onPaste={(e) => {
              // Coluna colada do Excel vem com quebras de linha, que o campo apagaria (juntando os SKUs): vira vírgula.
              const text = e.clipboardData.getData('text');
              if (!/[\r\n\t]/.test(text.trim())) return;
              e.preventDefault();
              const el = e.currentTarget;
              const pasted = text.trim().split(/[\r\n\t]+/).map((x) => x.trim()).filter(Boolean).join(', ');
              setSearch(el.value.slice(0, el.selectionStart ?? el.value.length) + pasted + el.value.slice(el.selectionEnd ?? el.value.length));
            }} />
        </label>
        <select className="select" aria-label="Categoria" value={params.get('category') ?? ''} onChange={(e) => setUrl({ category: e.target.value || null, page: null })}>
          <option value="">Todas as categorias</option>
          {cats.data?.map((c) => <option key={c.category} value={c.category}>{c.category} ({int(c.count)})</option>)}
        </select>
        <CostRange min={params.get('costMin')} max={params.get('costMax')} onChange={(costMin, costMax) => setUrl({ costMin, costMax, page: null })} />
        <select className="select" aria-label="Vínculo" value={params.get('status') ?? 'all'} onChange={(e) => setUrl({ status: e.target.value === 'all' ? null : e.target.value, page: null })}>
          <option value="all">Qualquer vínculo</option>
          <option value="matched">Vinculados</option>
          <option value="unmatched">Sem ficha central</option>
          <option value="no_gtin">Sem EAN</option>
          <option value="ambiguous">Ambíguos</option>
          <option value="kit">Kits</option>
        </select>
        <AttrFilterButton params={params} set={setUrl} />
        <select className="select" aria-label="Situação" value={params.get('active') ?? 'true'} onChange={(e) => setUrl({ active: e.target.value === 'true' ? null : e.target.value, page: null })}>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
          <option value="all">Ativos e inativos</option>
        </select>
        <span className="grow" />
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <button className="btn hide-mobile" type="button"><Columns3 size={16} /> Colunas</button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content className="menu" align="end" sideOffset={6}>
              <div className="menu-label">Colunas visíveis</div>
              {table.getAllLeafColumns().filter((c) => !c.columnDef.meta?.required).map((c) => (
                <Dropdown.Item key={c.id} className="menu-item" onSelect={(e) => { e.preventDefault(); toggleColumn(c.id, !c.getIsVisible()); }}>
                  {c.columnDef.meta?.label}
                  {c.getIsVisible() && <span className="ind"><Check size={16} /></span>}
                </Dropdown.Item>
              ))}
              <div className="menu-sep" />
              <Dropdown.Item className="menu-item" onSelect={() => { setSizing({}); updateTable(TABLE_ID, { hidden: defaultHidden, sizes: {} }); }}>
                Restaurar padrão
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>

      <div className="panel flush">
        {selected > 0 && (
          <div className="bulkbar" role="status">
            <b>{selected} selecionado{selected > 1 ? 's' : ''}</b>
            <span style={{ flex: 1 }} />
            {can(me.tenant.access, 'catalog', 'write') && (
              <button className="btn sm" type="button" onClick={() => setSyncing({ productIds: Object.keys(selection), label: `${selected} selecionado${selected > 1 ? 's' : ''}` })}>
                <RefreshCw size={14} /> Sincronizar anúncios
              </button>
            )}
            {can(me.tenant.access, 'catalog', 'write') && (
              <button className="btn sm" type="button" onClick={() => setMastering({ productIds: Object.keys(selection), label: `${selected} selecionado${selected > 1 ? 's' : ''}` })}>
                <Package size={14} /> Sincronizar com o mestre
              </button>
            )}
            {can(me.tenant.access, 'catalog', 'write') && (
              <button className="btn sm" type="button" onClick={() => setKitting({ productIds: Object.keys(selection), label: `${selected} selecionado${selected > 1 ? 's' : ''}` })}>
                <Boxes size={14} /> Criar kits
              </button>
            )}
            <button className="btn sm quiet" type="button" onClick={() => setSelection({})}><X size={14} /> Limpar</button>
          </div>
        )}

        {list.error ? (
          <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
        ) : !list.isPending && data.length === 0 ? (
          <div className="empty"><b>Nenhum produto encontrado</b>Mude a busca ou os filtros.</div>
        ) : (
          <>
            {/* tabela (computador e tablet) */}
            <div className="grid-wrap" ref={wrapRef} style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
              <table className="grid" style={{ width: baseTotal + extra }}>
                <colgroup>
                  {visible.map((c) => <col key={c.id} style={{ width: widthOf(c.id, c.getSize()) }} />)}
                </colgroup>
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((h) => {
                        const m = h.column.columnDef.meta!;
                        const sorted = m.sort && sort === m.sort ? dir : null;
                        return (
                          <th
                            key={h.id}
                            className={[m.num && 'num', m.sort && 'sortable', h.column.id in pinLeft && 'pin', h.column.id === 'title' && 'pin-edge'].filter(Boolean).join(' ')}
                            style={h.column.id in pinLeft ? { left: pinLeft[h.column.id] } : undefined}
                            onClick={m.sort ? () => onSort(m.sort!) : undefined}
                            aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                          >
                            <span className="th-in">
                              {flexRender(h.column.columnDef.header, h.getContext())}
                              {sorted === 'asc' && <ArrowUp size={13} />}
                              {sorted === 'desc' && <ArrowDown size={13} />}
                            </span>
                            {h.column.getCanResize() && (
                              <span
                                className={`resizer${h.column.getIsResizing() ? ' on' : ''}`}
                                onMouseDown={h.getResizeHandler()}
                                onTouchStart={h.getResizeHandler()}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={() => h.column.resetSize()}
                                role="separator"
                                aria-label={`Ajustar largura de ${m.label}`}
                              />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {list.isPending
                    ? Array.from({ length: 12 }, (_, i) => (
                        <tr key={i}>{visible.map((c) => <td key={c.id}><div className="skeleton" /></td>)}</tr>
                      ))
                    : table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className={row.getIsSelected() ? 'selected' : undefined} onClick={() => navigate(`/catalogo/${row.id}`)}>
                          {row.getVisibleCells().map((cell) => {
                            const m = cell.column.columnDef.meta!;
                            return (
                              <td
                                key={cell.id}
                                className={[m.num && 'num', cell.column.id in pinLeft && 'pin', cell.column.id === 'title' && 'pin-edge'].filter(Boolean).join(' ')}
                                style={cell.column.id in pinLeft ? { left: pinLeft[cell.column.id] } : undefined}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            {/* cartões (celular) */}
            <div className="cards">
              {data.map((p) => (
                <Link key={p.id} to={`/catalogo/${p.id}`} className="card-row">
                  <Thumb src={p.imageUrl} alt="" />
                  <div style={{ minWidth: 0 }}>
                    <div className={`t${p.title ? '' : ' untitled'}`}>{titleOf(p)}</div>
                    <div className="m">SKU {p.sku}{p.gtin && <> · {p.gtin}</>}</div>
                    <div style={{ marginTop: 4 }}><MatchTag status={p.matchStatus} /></div>
                  </div>
                  <div className="r">
                    <div>{int(p.available)} disp.</div>
                    <div className="muted">{brl(p.cost)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {total > 0 && (
          <div className="grid-foot">
            <span className="tnum">
              {int((page - 1) * pageSize + 1)}–{int(Math.min(page * pageSize, total))} de {int(total)}
            </span>
            <div className="pager">
              <label className="small">
                Por página{' '}
                <select className="select" style={{ height: 28 }} value={pageSize} onChange={(e) => { updateTable(TABLE_ID, { pageSize: Number(e.target.value) }); setUrl({ page: null }); }}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <button className="btn sm" type="button" disabled={page <= 1} onClick={() => setUrl({ page: page - 1 > 1 ? String(page - 1) : null })} aria-label="Página anterior"><ChevronLeft size={16} /></button>
              <span className="tnum">{int(page)} / {int(pages)}</span>
              <button className="btn sm" type="button" disabled={page >= pages} onClick={() => setUrl({ page: String(page + 1) })} aria-label="Próxima página"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/** Faixa de custo (o preço do catálogo): aplica ao sair do campo ou com Enter. */
function CostRange({ min, max, onChange }: { min: string | null; max: string | null; onChange: (min: string | null, max: string | null) => void }) {
  const [a, setA] = useState(min ?? '');
  const [b, setB] = useState(max ?? '');
  useEffect(() => { setA(min ?? ''); setB(max ?? ''); }, [min, max]);
  const clean = (v: string) => { const n = Number(v.replace(',', '.')); return v.trim() && Number.isFinite(n) && n >= 0 ? String(n) : null; };
  const apply = () => { if (clean(a) !== min || clean(b) !== max) onChange(clean(a), clean(b)); };
  const key = (e: React.KeyboardEvent) => { if (e.key === 'Enter') apply(); };
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }} title="Faixa de custo (R$)">
      <input className="input" style={{ width: 92 }} inputMode="decimal" placeholder="Custo de" aria-label="Custo mínimo" value={a} onChange={(e) => setA(e.target.value)} onBlur={apply} onKeyDown={key} />
      <span className="muted small">a</span>
      <input className="input" style={{ width: 92 }} inputMode="decimal" placeholder="até" aria-label="Custo máximo" value={b} onChange={(e) => setB(e.target.value)} onBlur={apply} onKeyDown={key} />
    </span>
  );
}

/** Kit de N unidades para todos os produtos da seleção (marcados ou o filtro atual). */
function BulkKitDialog({ selection, onClose }: { selection: ContentSelection; onClose: () => void }) {
  const qc = useQueryClient();
  const [units, setUnits] = useState('3');
  const [discount, setDiscount] = useState('');
  const run = useMutation({
    mutationFn: () => api<{ selected: number; created: number; skipped: number; errors: { productId: string; message: string }[] }>('/catalog/kits/bulk', {
      method: 'POST',
      json: {
        units: Number(units),
        ...(discount.trim() ? { discountPct: Number(discount.replace(',', '.')) } : {}),
        ...('productIds' in selection ? { productIds: selection.productIds } : { filter: selection.filter }),
      },
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); qc.invalidateQueries({ queryKey: ['summary'] }); },
  });
  const n = Number(units);
  const valid = Number.isInteger(n) && n >= 2 && n <= 100;
  return (
    <Modal title="Criar kits em lote" onClose={onClose}>
      {run.data ? (
        <>
          <p style={{ margin: 0 }}>
            <b>{int(run.data.created)} kit{run.data.created === 1 ? '' : 's'} de {units} criado{run.data.created === 1 ? '' : 's'}</b> de {int(run.data.selected)} produto(s).
            {run.data.skipped > 0 && <> {int(run.data.skipped)} ficaram de fora (já tinham kit de {units}, são kits ou estão inativos).</>}
          </p>
          {run.data.errors.length > 0 && <p className="small" style={{ margin: 0, color: 'var(--err)' }}>{run.data.errors.length} com erro: {run.data.errors.slice(0, 5).map((e) => e.message).join('; ')}</p>}
          <p className="small muted" style={{ margin: 0 }}>Os kits já nascem com preço e estoque nos canais, calculados a partir do produto base. Para publicar, use as telas de publicação de cada canal.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn primary" type="button" onClick={onClose}>Concluir</button></div>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>Produtos: <b>{selection.label}</b>. Um kit para cada um (SKU base + "-K{valid ? n : 'N'}").</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div className="field"><label htmlFor="kit-units">Unidades por kit</label>
              <input id="kit-units" className="input" style={{ width: 120 }} inputMode="numeric" value={units} onChange={(e) => setUnits(e.target.value)} /></div>
            <div className="field"><label htmlFor="kit-disc">Desconto no kit (%), opcional</label>
              <input id="kit-disc" className="input" style={{ width: 160 }} inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
          </div>
          {run.error && <ErrorBox error={run.error} />}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn" type="button" onClick={onClose}>Cancelar</button>
            <button className="btn primary" type="button" disabled={!valid || run.isPending} onClick={() => run.mutate()}><Boxes size={16} /> {run.isPending ? 'Criando…' : `Criar kits de ${valid ? n : '…'}`}</button>
          </div>
        </>
      )}
    </Modal>
  );
}
